import { EventRoleApiToken } from "@/types/EventRoleApiToken";
import {
  EventRoleApiTokenCreationDTO,
  EventRoleApiTokenCreationResponseDTO,
} from "@/types/EventRoleApiTokenCreationDTO";
import { apiGet, apiPost, rethrowApi } from "../_apiShim";
import { api, PREVIEW_BYPASS_HEADER } from "../client";

const BASE_PATH = "/api/v1/event-role-api-tokens/";

/**
 * B2-0 audit r1 (store LOW, chain liveness): the preview's token routes — the
 * mint (start, switch, re-mint) and the revoke — give up after 30 s. Start,
 * switch and re-mint run as ONE chain in `previewStore`, and `api` has no
 * timeout: one mint that never answered held every later start, switch and
 * re-mint (after Exit, a new Start sent nothing). The axios timeout
 * (`ECONNABORTED`) is a "no answer" there, so the existing forced path takes
 * over. Direct `api` calls (same shape as `apiPost` / `apiDelete`: errors
 * through `rethrowApi`) because the shim's helpers take no request config.
 */
export const PREVIEW_TOKEN_TIMEOUT_MS = 30_000;

export async function listEventRoleApiTokens(
  eventId: string,
): Promise<EventRoleApiToken[]> {
  return await apiGet<EventRoleApiToken[]>(
    `${BASE_PATH}${eventId}`,
    { "Cache-Control": "no-store" },
    true,
  );
}

/**
 * Mint a PAT. The response carries the plaintext exactly ONCE — caller MUST
 * show it to the user (with copy-to-clipboard) and then discard.
 */
export async function createEventRoleApiToken(
  eventId: string,
  templateId: string,
  body: EventRoleApiTokenCreationDTO,
): Promise<EventRoleApiTokenCreationResponseDTO> {
  return await apiPost<EventRoleApiTokenCreationResponseDTO>(
    `${BASE_PATH}${eventId}/templates/${templateId}`,
    body,
    {},
    true,
  );
}

/**
 * Revoke a PAT. `bypassPreview` (B2-0, PB-FB-H3-PREV-REVOKE): a revoke issued
 * WHILE a preview session is live — the re-mint's superseded token, the stale
 * guard's own fresh token, a role switch's predecessor, and the stop (a new
 * session may start before its replay) — must ride the admin's cookie. The
 * revoke route does not admit the preview bearer (`EVENT-CUSTOM`), so without
 * the flag it answered 403, was swallowed, and the superseded token lived on.
 *
 * No body — the old `apiDelete(url, true)` third argument landed as the
 * DELETE body. 30 s timeout: see {@link PREVIEW_TOKEN_TIMEOUT_MS}.
 */
export async function revokeEventRoleApiToken(
  eventId: string,
  tokenId: string,
  bypassPreview = false,
): Promise<void> {
  const headers: Record<string, string> = {};
  if (bypassPreview) headers[PREVIEW_BYPASS_HEADER] = "1";
  try {
    await api.delete(`${BASE_PATH}${eventId}/${tokenId}`, {
      headers,
      timeout: PREVIEW_TOKEN_TIMEOUT_MS,
    });
  } catch (e) {
    rethrowApi(e);
  }
}

const EVENT_ROLES_PATH = "/api/v1/event-roles/";

export interface PreviewTokenResponse {
  eventRoleApiTokenId: string;
  token: string;
  expiresAt: string;
  /** B2-0: whether the minted token APPLIES a scope override. The current
   *  backend always sends it; an older one (rollback, mixed instances) does
   *  not — the store then treats an override mint as failed (gate M3). */
  scopeOverride?: boolean;
  /** Present only when the token applies a non-null field list. Display only:
   *  a re-mint never adopts server state (the key comes from the inputs). */
  fieldAllowlist?: string[] | null;
}

/**
 * Mint an ephemeral, read-only "preview as role" PAT. Exactly one of
 * `templateId` / `baseRole` must be supplied. The plaintext `token` is returned
 * ONCE — the caller holds it in memory only (never localStorage) and revokes it
 * via {@link revokeEventRoleApiToken} on exit.
 *
 * Live capability editing (template previews only): pass `capabilities` — the
 * read-only cap set the edited preview should expose. The backend keeps the
 * template's row/field scope but swaps the cap set to this list (masked to
 * read-only). Pass `expiresAt` (the current session expiry, ISO-8601) so the
 * re-mint PRESERVES the running countdown instead of restarting it — the backend
 * clamps it to at most a fresh TTL as the security backstop.
 */
export async function createPreviewToken(
  eventId: string,
  target: {
    templateId?: string;
    baseRole?: string;
    capabilities?: string[];
    /** Live SCOPE override (template previews). When `scopeOverride` is true the
     *  backend uses these instead of the template's row/field scope. */
    scopeOverride?: boolean;
    /** With `scopeOverride: true` BOTH keys must be present (the backend
     *  answers 400 `previewTargetAmbiguous` otherwise): `rowFilters: null` =
     *  the template's rows LIVE, `[]` = no row restriction; `fieldAllowlist:
     *  null` = all fields. `null` survives JSON, `undefined` would vanish. */
    rowFilters?: unknown[] | null;
    fieldAllowlist?: string[] | null;
    expiresAt?: string;
    /** The live token this mint replaces — sent only by the tab that minted it.
     *  A valid one is CAS-shortened to 60 s server-side and the mint is charged
     *  to the re-mint bucket instead of the fresh one. */
    supersedesTokenId?: string;
  },
  /** Mint under the admin's own auth instead of any active preview bearer. REQUIRED
   *  for an in-banner role SWITCH: a swap that keeps the old preview active would
   *  otherwise mint role B's token while riding role A's read-only bearer (403 /
   *  privilege escalation). See client.ts `X-Preview-Bypass`. */
  bypassPreview = false,
): Promise<PreviewTokenResponse> {
  const headers: Record<string, string> = {};
  if (bypassPreview) headers[PREVIEW_BYPASS_HEADER] = "1";
  try {
    const r = await api.post<PreviewTokenResponse>(
      `${EVENT_ROLES_PATH}${eventId}/preview-token`,
      target,
      { headers, timeout: PREVIEW_TOKEN_TIMEOUT_MS },
    );
    return r.data;
  } catch (e) {
    rethrowApi(e);
  }
}
